import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactCrop, { centerCrop } from "react-image-crop";
import { canvasToFile } from "utils/constant";
import "react-image-crop/dist/ReactCrop.css";

/**
 * Reads the image source and, if it is a GIF (checked by magic bytes, so it
 * works for blob:/data: URLs with no extension), returns the original Blob.
 * Returns null for non-GIFs or if the source can't be read (e.g. CORS).
 */
const loadGifBlob = async (src) => {
  try {
    const res = await fetch(src);
    const blob = await res.blob();
    const header = await blob.slice(0, 4).text(); // "GIF8" for GIF87a / GIF89a
    return header === "GIF8" ? blob : null;
  } catch (e) {
    return null;
  }
};

export default function CropProfile({
  setParentCrop = () => {},
  setCropPreview = () => {},
  // NEW: receives crop rectangle in ORIGINAL image pixels when the image is a GIF,
  // or null for other formats. Send it to the backend with the original file.
  setParentCropData = () => {},
  locked = false,
  ...props
}) {
  const { image, type, returnType } = props;
  const imgSrc = image;
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [gifBlob, setGifBlob] = useState(null);
  const zoom = 1;
  const [aspect, setAspect] = useState(undefined);

  // Detect GIFs whenever the source changes
  useEffect(() => {
    let cancelled = false;
    setGifBlob(null);
    if (imgSrc) {
      loadGifBlob(imgSrc).then((blob) => {
        if (!cancelled) setGifBlob(blob);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [imgSrc]);

  const getCropData = useCallback(async () => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current) {
      const el = imgRef.current;

      // Static frame is still used for the preview (and for non-GIF output)
      const { blobImage, image, base64Canvas } = await canvasToFile(
        el,
        completedCrop,
        zoom,
        0,
        "crop-image-free-mode",
        type
      );

      if (gifBlob) {
        // GIF: do NOT use the canvas result (it is a single flat frame).
        // Return the untouched original plus coordinates for server-side cropping.
        const scaleX = el.naturalWidth / el.width;
        const scaleY = el.naturalHeight / el.height;

        const x = Math.max(0, Math.round(completedCrop.x * scaleX));
        const y = Math.max(0, Math.round(completedCrop.y * scaleY));
        const width = Math.min(el.naturalWidth - x, Math.round(completedCrop.width * scaleX));
        const height = Math.min(el.naturalHeight - y, Math.round(completedCrop.height * scaleY));

        const gifFile = new File([gifBlob], "profile.gif", { type: "image/gif" });

        setParentCrop(returnType === "blob" ? gifBlob : gifFile);
        setParentCropData({ x, y, width, height });
        if (base64Canvas) setCropPreview(base64Canvas);
        return;
      }

      if (blobImage) {
        setParentCropData(null);
        if (returnType === "blob") {
          setParentCrop(blobImage);
        } else {
          setParentCrop(image);
        }
        setCropPreview(base64Canvas);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCrop, zoom, gifBlob]);

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    let cropSize = Math.min(width, height);
    let defaultM = {
      width: 180,
      height: 180,
    };
    let finalW = defaultM.width;
    let finalH = defaultM.height;

    //calculate aspect ratio
    let aw = finalH / finalW;
    let aspectRatioW = aw * defaultM.width;

    let ah = finalW / finalH;
    let aspectRatioH = ah * defaultM.height;

    //set aspect ratio
    let aspectRatio = aspectRatioH / aspectRatioW;
    setAspect(aspectRatio);

    setCrop(
      centerCrop(
        {
          unit: "px",
          width: cropSize - 10,
          height: cropSize - 10,
        },
        width,
        height
      )
    );
  }

  useEffect(() => {
    getCropData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCrop, gifBlob]);

  return (
    <div className="min-w-[375px]">
      <div
        className="w-full relative bg-passwordGray rounded-md flex justify-center items-center"
        style={{ minHeight: "400px" }}
      >
        <div
          className="w-full overflow-y-auto custom-scroll flex justify-center items-center"
          style={{ maxHeight: "73vh" }}
        >
          {!!imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              locked={locked}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                onLoad={onImageLoad}
                style={{ maxHeight: "400px", maxWidth: "400px", objectFit: "cover" }}
              />
            </ReactCrop>
          )}
        </div>
      </div>
    </div>
  );
}
